"use client";

import HCaptcha from "@hcaptcha/react-hcaptcha";
import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_COUNTRY, storageUserCountry } from "@utils/default";
import { mailRegex, nameRegex, phoneRegex } from "@utils/textUtils";
import { trimUnicodeString } from "@utils/trimText";
import { getCookie, setCookie } from "cookies-next";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { InferType, boolean, date, object, string } from "yup";

import TermsAndPrivacy from "@/components/Checkout/TermsAndPrivacy";
import { handleCreateLeadSubmit, handleMetaverseSlotsUpdate } from "@/components/CreateLeadModal/actions";
import Button, { ButtonSize, ButtonTheme } from "@/components/ui/Button/Button";
import Input from "@/components/ui/Form/Input/Input";
import RecaptchaForm from "@/components/ui/Form/RecaptchaForm/RecaptchaForm";
import Telephone from "@/components/ui/Form/Telephone/Telephone";
import { BannerContext } from "@/context/BannerContext";
import { LoadingContext } from "@/context/LoaderContext";
import { ModalsContext } from "@/context/ModalsContext";
import { UTMParametersContext, getFallbackCampaignParameters, utmParametersMapping } from "@/context/UTMParametersContext";
import { leadFormSubmission } from "@/dataLayer/dataLayer";
import useLeadTrackings from "@/hooks/useLeadTrackings";
import { usePageLocationAsPerPath } from "@/hooks/usePageLocationAsPerPath";
import { normalizeCountryName, prefixList } from "@/utils/data/prefixes";
import { countries } from "@/utils/data/prefixes";

import CheckBox from "../ui/Form/CheckBox/CheckBox";
import Select from "../ui/Form/Select/Select";

// Get supported timezones
const timezones = Intl.supportedValuesOf("timeZone");

// Helper function to get the minimum date based on current time
const getMinDateString = () => {
  const now = new Date();
  const currentHour = now.getHours(); // Get the current hour (0-23)

  if (currentHour >= 18) {
    // 18 represents 6:00 PM
    // If current time is 6 PM or later, minimum selectable date is tomorrow
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1); // Add one day
    return tomorrow.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  } else {
    // If current time is before 6 PM, minimum selectable date is today
    return now.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  }
};

const LeadFormSchema = object({
  token: string().required("form.error.token.required"),
  firstName: string().matches(nameRegex, "form.error.firstname.invalid").required("form.error.firstname.required"),
  lastName: string().matches(nameRegex, "form.error.lastname.invalid").required("form.error.lastname.required"),
  phone: string()
    .test("is-valid-phone", "form.error.phone.invalid", function (value) {
      const { prefix } = this.parent; // Access prefix (country code) from the parent form
      let countryCode = prefix ? prefix.split("_").find((i: string) => i[0] == "+") : "+971";
      const fullPhoneNumber = countryCode + (value || ""); // Combine the prefix and phone number

      return isValidPhoneNumber(fullPhoneNumber); // Validate with the full phone number
    })
    .required("form.error.phone.required"),
  prefix: string().required("form.error.prefix.required"),
  email: string().matches(mailRegex, "form.error.email.invalid").required("form.error.email.required"),
  countryID: string(),
  // date/time fields
  contactDate: string().matches(/^\d{4}-\d{2}-\d{2}$/, "form.error.contactDate.invalid"),
  // .required("form.error.contactDate.invalid"),
  contactTime: string(),
}).required();

// as per request on https://dev.azure.com.mcas.ms/damacgroupdevops/DPWeb_TO_Ecom_Dev/_workitems/edit/93472
// separate the complete lead form schema for future implementation
const ExtendedLeadFormSchema = LeadFormSchema.shape({
  newsletterSubscription: boolean(),
  privacy: boolean().required("form.error.privacy.required"),
  contactDate: string().matches(/^\d{4}-\d{2}-\d{2}$/, "form.error.contactDate.invalid"),
  // .required("form.error.contactDate.invalid"),

  contactTime: string(),
  timezone: string(),
})
  .test("contact-date-time-in-the-future", "form.error.contactDateTime", (value) => {
    if (!value.contactDate || !value.contactTime || !value.timezone) {
      return true;
    }
    try {
      // Ensure value.contactDate is a Date object
      const selectedDate =
        typeof value.contactDate === "object" && value.contactDate !== null && "getTime" in value.contactDate
          ? (value.contactDate as Date)
          : new Date(value.contactDate);
      const selectedTime = value.contactTime; // "HH:mm:ss" string
      const selectedTimezone = value.timezone; // Timezone string

      const nowInSelectedTimezone = new Date().toLocaleString("en-US", { timeZone: selectedTimezone });

      const isoDatePart = selectedDate.toISOString().split("T")[0]; // YYYY-MM-DD
      const timePart = selectedTime.split(".")[0]; // HH:mm:ss (strip potential milliseconds)

      // Get the offset for the selected date and time in the chosen timezone
      const dateTimeFormat = new Intl.DateTimeFormat("en-US", {
        timeZone: selectedTimezone,
        timeZoneName: "longOffset",
      });
      // Create a temporary Date object in the selected date/time (interpreted as local) to get offset
      const dummyDateForOffset = new Date(`${isoDatePart}T${timePart}`);
      const gmtOffsetMatch = dateTimeFormat.format(dummyDateForOffset).match(/GMT([+-]\d{1,2}:?\d{0,2})/);
      const gmtOffset = gmtOffsetMatch ? gmtOffsetMatch[1] : "+00:00"; // Default to UTC offset if extraction fails

      // Construct the date string with the offset
      const selectedDateTimeStringWithOffset = `${isoDatePart}T${timePart}${gmtOffset}`;

      // Create a Date object from this string (which is interpreted correctly with the offset)
      const selectedDateTime = new Date(selectedDateTimeStringWithOffset);

      // Get the current time as a Date object
      const now = new Date();

      // Check if the selected date/time moment is strictly after the current moment
      return selectedDateTime.getTime() > now.getTime();
    } catch (error) {
      console.error("Date/Time validation error:", error);
      return false;
    }
  })
  .required();

export type LeadFormSchemaType = InferType<typeof LeadFormSchema>;
export type ExtendedLeadFormSchemaType = InferType<typeof ExtendedLeadFormSchema>;

type Props = {
  mode: string;
  closeModal: () => void;
  leadFormPageLocation?: string;
  isStickleadForm?: boolean;
};

// https://stackoverflow.com/a/64572418
// const languageNames = new Intl.DisplayNames(["en"], { type: "language" });

export default function CreateLeadForm({ mode, closeModal, leadFormPageLocation, isStickleadForm }: Props) {
  const t = useTranslations();
  const pathname = usePathname();
  const locale = useLocale();
  const hcaptchaRef = useRef<any>(null);
  const [language, _] = locale.split("-");
  const { data: session } = useSession();
  const { additionalInfo, settings } = useContext(ModalsContext);
  const { utmParameters } = useContext(UTMParametersContext);
  const { loading } = useContext(LoadingContext);
  const { setSuccessMessage, setErrorMessage } = useContext(BannerContext);
  const cookieUserCountry = getCookie(storageUserCountry);
  const [brochureItem, setBrochureItem] = useState("");
  const [hcaptchaToken, setHcaptchaToken] = useState<string | null>(null);

  // State to hold available contact times, copied from the first component
  const [contactTimes, setContactTimes] = useState<{ label: string; value: string }[]>([]);

  // Calculate the minimum date for the date picker based on the current time
  const minDateForPicker = useMemo(() => {
    return getMinDateString();
  }, []); // Recalculate only if getMinDateString logic changes (effectively once on mount)

  // Trim the title to 100 charaters - start
  const leadFormTitle = `${typeof document !== "undefined" ? document.title : ""} Lead Form`;
  const trimmedLeadFormTitle = trimUnicodeString(leadFormTitle, 100);
  const locationName = usePageLocationAsPerPath(leadFormPageLocation);
  // Trim the title to 100 charaters - end

  const handleTouchStart = () => {
    if (hcaptchaRef.current) {
      hcaptchaRef.current.execute(); // This will show a new challenge
    }
  };

  const trackingFormName = mode === "webImmersiveLead" ? "get_in_touch_metaverse" : mode === "getInTouch" ? "get_in_touch" : mode;

  const initialState = useMemo(() => {
    const parameters: { [key: string]: string } = {
      ...getFallbackCampaignParameters(utmParameters?.utm_medium),
      ...utmParameters,
      // lastMileConversion: `${pathname?.replaceAll("/", "") === locale ? "Home page" : document.title} Lead Form`,
      lastMileConversion: locationName,
      projectStatus: additionalInfo?.project_status,
    };

    const initialStateDefinition = {
      mode,
      utmData: Object.fromEntries(Object.entries(utmParametersMapping).map(([k, v]) => [v, parameters[k] ?? ""])),
      brochureData: settings.brochureData,
      additionalInfo,
    } as LeadPayload;

    // if we are in enquire mode get the projectName from stickyBarData session storage
    if (mode === "enquire" && typeof window !== "undefined") {
      const sessionStorageData = sessionStorage.getItem("stickyBarData");
      if (sessionStorageData) {
        const stickyBarData = JSON.parse(sessionStorageData);
        initialStateDefinition.projectName = stickyBarData.projectName;
      }
    }
    return initialStateDefinition;
  }, [additionalInfo, locale, mode, pathname, settings.brochureData, utmParameters, trimmedLeadFormTitle]); // Added trimmedLeadFormTitle dependency

  const formDefaultValues = useMemo(() => {
    const currentCountry = cookieUserCountry?.toUpperCase() ?? DEFAULT_COUNTRY ?? locale.slice(3).toUpperCase();
    const userPrefix = isolatePrefix(session?.user?.mobile);
    const defaultPrefix = prefixList.find((prefix) => prefix.code === currentCountry);
    return {
      firstName: session?.user?.fullName?.split(" ")?.[0] ?? "",
      lastName: session?.user?.fullName?.split(" ")?.at(-1) ?? "",
      phone: session?.user?.mobile ? session?.user?.mobile.slice(userPrefix?.length) : "",
      prefix: session?.user?.mobile
        ? userPrefix + "_" + prefixList.find((prefix) => prefix.dial_code === userPrefix)?.name
        : defaultPrefix?.dial_code + "_" + defaultPrefix?.name,
      countryID: defaultPrefix?.code,
      email: session?.user?.email ?? "",
      // Date and time
      contactTime: "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    };
  }, [locale, session?.user?.email, session?.user?.fullName, session?.user?.mobile]);
  console.log("formDefaultValues", formDefaultValues);
  const { control, setValue, getValues, watch, formState, handleSubmit, clearErrors, setError, reset } = useForm<LeadFormSchemaType>({
    resolver: yupResolver(LeadFormSchema),
    defaultValues: formDefaultValues,
    mode: "onBlur",
  });

  useLeadTrackings({
    mode,
    getValues,
    formState,
  });

  // Check for the combined date/time validation error
  const invalidContactDateTime =
    formState.errors.contactDate?.type === "contact-date-time-in-the-future"
      ? formState.errors.contactDate
      : formState.errors.contactTime?.type === "contact-date-time-in-the-future"
        ? formState.errors.contactTime
        : Object.values(formState.errors).find((err: any) => err.type === "contact-date-time-in-the-future");

  const currentPhoneprefix = watch("prefix");
  const currentCountryID = watch("countryID");
  const contactDate = watch("contactDate");
  // const timezone = watch("timezone");

  // get the country code

  const getCountryCode = (locale: string) => {
    if (locale.includes("-")) {
      return locale.split("-")[1].toUpperCase();
    }
    return locale.toUpperCase();
  };
  // update the date and time if time zone changed

  // useEffect(() => {
  //   setValue("contactDate", "");
  //   setValue("contactTime", "");
  // }, [timezone, setValue]);

  // Set hCaptcha token in form value when received
  useEffect(() => {
    console.log("hCaptcha token received:", hcaptchaToken);
    if (hcaptchaToken) {
      console.log("hCaptcha token received:", hcaptchaToken);
      setValue("token", hcaptchaToken);
      clearErrors("token");
    }
  }, [hcaptchaToken, setValue]);

  // update the contactTimes on contactDate change
  useEffect(() => {
    if (contactDate && typeof contactDate === "string") {
      // Ensure contactDate is a string compatible with ISO format
      // if (mode === "metaverseWalkthrough") {
      //   // Handle metaverse slots update logic
      //   handleMetaverseSlotsUpdate(additionalInfo.pixelStreamingProjectId, contactDate).then((times) => setContactTimes(times));
      // } else {
      // Logic to generate general contact times
      const now = new Date();
      const todayString = now.toISOString().substring(0, 10);
      const selectedDateObj = new Date(contactDate); // Create date object from the string

      // const formatter = new Intl.DateTimeFormat("en-US", {
      //   timeZone: timezone || "UTC",
      //   hour: "2-digit",
      //   minute: "2-digit",
      //   second: "2-digit",
      //   hour12: false,
      // });

      // const parts = formatter.formatToParts(new Date());
      // const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
      // const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
      // const second = parts.find((p) => p.type === "second")?.value ?? "00";

      const minTime =
        contactDate === todayString
          ? // If selected date is today, set min time to current time (rounded down to nearest second)
            `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now.getSeconds().toString().padStart(2, "0")}`
          : // If selected date is in the future, set min time to 10:00:00
            "09:00:00";

      // const minTime = contactDate === todayString ? `${hour}:${minute}:${second}` : "09:00:00";

      setContactTimes(
        [...Array(17).keys()] // Generates times from 09:00 up to 21:00 (18 half-hour intervals starting from 09:00)
          .map((x) => {
            const hours = 9 + Math.floor(x / 2);
            const minutes = x % 2 ? 30 : 0;
            const hh = hours.toString().padStart(2, "0");
            const mm = minutes.toString().padStart(2, "0");
            return {
              label: `${hh}:${mm}`,
              value: `${hh}:${mm}:00`, // Value includes seconds
            };
          })
          // Filter out times that are earlier than the calculated minTime
          .filter((timeOption) => timeOption.value.localeCompare(minTime) >= 0),
      );
      // }
    } else {
      // Handle case where contactDate is not a valid string or is null
      setContactTimes([]); // Clear times if date is invalid
      setValue("contactTime", ""); // Clear selected time
    }
    // Added dependencies
  }, [additionalInfo?.pixelStreamingProjectId, contactDate, mode, setValue]);

  // when contactTimes updates set the first as contactTime
  useEffect(() => {
    // Only set value if there are contact times available and the current value is not already the first one
    if (contactTimes.length > 0 && getValues("contactTime") !== contactTimes[0]?.value) {
      setValue("contactTime", contactTimes[0]?.value);
    } else if (contactTimes.length === 0 && getValues("contactTime") !== "") {
      setValue("contactTime", "");
    }
  }, [contactTimes, setValue, getValues]);

  useEffect(() => {
    const country = prefixList.find((item) => `${item.dial_code}_${normalizeCountryName(item.name)}` === currentPhoneprefix);
    if (country && currentCountryID != country.code) {
      setValue("countryID", country.code);
    }
  }, [currentCountryID, currentPhoneprefix, setValue]);

  const [triggerRigenerate, setTriggerRigenerate] = useState(false);

  const regenerateToken = () => {
    setTriggerRigenerate((prev) => !prev);
  };

  const onSubmit = async (data: any) => {
    loading(true);
    console.info("CreateLeadForm onSubmit data", data);
    const response = await handleCreateLeadSubmit({
      ...initialState,
      ...data,
      ...(typeof brochureItem === "object" && brochureItem !== null ? { brochureData: brochureItem } : {}),
    });

    // console.log("response ======>", response);

    if (!response.error) {
      // console.log("Error ====>", response);
      loading(false);
      reset(formDefaultValues);
      setHcaptchaToken(null);
      setValue("token", "");
      hcaptchaRef.current?.resetCaptcha();
      leadFormSubmission({
        step_number: 4,
        step_name: "form success",
        form_name: trackingFormName,
        cta_name: t("form.label.submitpreference"),
        user_logged_in: !!session,
        // user_id: session?.user.id || "",
        // first_name: data.firstName || "", // 93506 - Removed as per ticket - First Name, Last Name, Email, Phone
        // last_name: data.lastName || "",
        // email: data.email || "",
        // phone: data.phone,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
        ...additionalInfo,
      });
      leadFormSubmission({
        step_number: 5,
        step_name: "sf lead success",
        form_name: trackingFormName,
        cta_name: t("form.label.submitpreference"),
        user_logged_in: !!session,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
        // user_id: session?.user.id || "",
        // first_name: data.firstName || "", // 93506 - Removed as per ticket - First Name, Last Name, Email, Phone
        // last_name: data.lastName || "",
        // email: data.email || "",
        // phone: data.phone,
        ...additionalInfo,
      });
      closeModal();
      setSuccessMessage(t("label.request.success"));
    } else {
      regenerateToken();
      loading(false);
      setErrorMessage(response.error.message || t("label.error.generic"));
      leadFormSubmission({
        step_number: 1,
        step_name: "form error",
        form_name: trackingFormName,
        error_text: response?.error?.message || "",
        error_field: response?.error?.message || "",
        user_logged_in: !!session,
        // user_id: session?.user.id || "",
        // first_name: data.firstName || "", // 93506 - Removed as per ticket - First Name, Last Name, Email, Phone
        // last_name: data.lastName || "",
        // email: data.email || "",
        // phone: data.phone,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
        ...additionalInfo,
      });
    }
  };

  useEffect(() => {
    setBrochureItem(settings.brochureData);
  }, []);
  return (
    <RecaptchaForm onSubmit={handleSubmit(onSubmit)} noValidate setValue={setValue} name={trackingFormName} regenerateToken={triggerRigenerate}>
      <div className={`flex flex-col  ${isStickleadForm ? "" : "md:grid md:grid-cols-2  gap-s"}`}>
        <div className={`col-span-1 ${isStickleadForm ? "" : "pb-xs"}`}>
          <Input control={control} name="firstName" label={`${t("form.label.firstName")}`} required />
        </div>
        <div className={`col-span-1 ${isStickleadForm ? "" : "pb-xs"}`}>
          <Input control={control} name="lastName" label={`${t("form.label.lastName")}`} required />
        </div>

        <div className={`col-span-1 ${isStickleadForm ? "" : "pb-xs"}`}>
          <Input control={control} name="email" label={`${t("form.label.email")}`} required maxLength={255} />
        </div>
        <div className={`col-span-1 ${isStickleadForm ? "" : "pb-xs"}`}>
          <Telephone
            phoneName="phone"
            prefixName="prefix"
            control={control}
            showCountryCodeLeadPrefixes={true}
            controlledCountryCode={currentCountryID}
          />
        </div>

        {/* <div className="col-span-1 pb-xs">
          <Select
            control={control}
            name="timezone"
            label={`${t("form.label.timezone")}`}
            options={timezones.map((tz) => ({ label: tz, value: tz }))}
            value={Intl.DateTimeFormat().resolvedOptions().timeZone}
          />
        </div> */}

        {/* Contact Date Input */}
        {/* <div className="col-span-1 pb-xs">
          <Input
            control={control}
            type="date"
            name="contactDate"
            label={t("form.label.date")}
            // min={new Date().toISOString().split("T")[0]}
            min={minDateForPicker} // Use the calculated minimum date string
            // required
          />
        </div> */}
        {/* Contact Time Select */}
        {/* <div className="col-span-1 pb-xs">
          <Select
            control={control}
            name="contactTime"
            label={t("form.label.timeframe")}
            options={contactTimes}
            disabled={contactTimes.length === 0}
            // required
          />
        </div> */}

        {/* Display combined date/time validation error message */}
        {!!invalidContactDateTime && (
          <span className={`block text-xs leading-tight font-secondary text-[#B42318] pt-xs px-xxs`}>
            {t(invalidContactDateTime.message as string)}
          </span>
        )}
        {/* <div className="col-span-1 pb-xs">
          <Select control={control} name="preferredLanguage" label={t("form.label.preferredlanguages")} options={preferredLanguages} />
        </div>
        <div className="col-span-1 pb-xs">
          <Select control={control} name="currentCountry" label={t("form.label.location")} options={countries} />
        </div>
        <div className="col-span-1 pb-xs">
          <Input control={control} type="date" name="contactDate" label={t("form.label.date")} min={new Date().toISOString().split("T")[0]} />
        </div>
        <div className="col-span-1 pb-xs">
          <Select control={control} name="contactTime" label={t("form.label.timeframe")} options={contactTimes} disabled={contactTimes.length == 0} />
        </div>

        {!!invalidContactDateTime && (
          <span className={`block h-m text-xs leading-[7px] font-secondary text-[#B42318] pt-xs px-xxs`}>{t(invalidContactDateTime.message)}</span>
        )}

        <div className="col-span-1 pb-xs">
          <Select
            control={control}
            name="timezone"
            label={`${t("form.label.timezone")}`}
            options={timezones.map((tz) => ({ label: tz, value: tz }))}
            value={Intl.DateTimeFormat().resolvedOptions().timeZone}
          />
        </div>

        <div className="col-span-2 mt-s">
          <p className="text font-secondary font-medium mb-l">{t("form.label.preferredmethod")}</p>
          <div className="grid grid-cols-2 md:flex gap-xs">
            {((settings?.contactMethods ?? ["SMSConsent", "WhatsappConsent"]) as string[]).map((x) => (
              <CheckBox
                key={x}
                control={control}
                label={`${t(`form.label.${x}`.toLowerCase().replace(/consent$/, ""))}`}
                name={`contactMethod.${x}`}
              />
            ))}
          </div>
        </div>

        {!!missingMethodSelection && (
          <span className={`block h-m text-xs leading-[7px] font-secondary text-[#B42318] pt-xs px-xxs`}>{t(missingMethodSelection.message)}</span>
        )}

        <div className="col-span-2 pt-m">
          <CheckBox control={control} label={`${t("form.label.newsletterSubscription")}`} name="newsletterSubscription" />
        </div> */}
        <div onTouchStart={handleTouchStart}>
          <HCaptcha
            ref={hcaptchaRef}
            sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY!}
            onExpire={() => setValue("token", "")}
            onVerify={setHcaptchaToken}
            languageOverride={language}
          />

          {!!formState.errors.token && (
            <span className="block text-xs leading-tight font-secondary text-[#B42318] pt-xs px-xxs">
              {t(formState.errors.token.message as string)}
            </span>
          )}
        </div>
        <div className="col-span-2">
          <Button
            type="submit"
            size={ButtonSize.MEDIUM}
            theme={ButtonTheme.BLACK}
            customClass={`border-none w-full ${isStickleadForm ? "" : "md:w-auto"} mt-m`}
            disabled={formState.isSubmitting}
          >
            {isStickleadForm ? t("form.label.submitRequest") : t("form.label.submitpreference")}
          </Button>
        </div>
      </div>

      <div className={`${isStickleadForm ? "" : "mt-xl"}`}>
        {/* {false && (
          <>
            <p className="text font-secondary font-medium mb-l">{t("form.label.assistance")}</p>
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.PRIMARY} theme={ButtonTheme.WHITE} href="" customClass="mr-m">
              <span className="flex items-center gap-xs ">
                <Icon name="chat" size="16" title="mail" />
                {t("form.label.chat")}
              </span>
              <span className="sr-only">{t("form.label.chat")}</span>
            </Button>
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.PRIMARY} theme={ButtonTheme.WHITE} href="" customClass="mr-m">
              <span className="flex items-center gap-xs ">
                <Icon name="social-whatsapp" size="16" title="mail" />
                {t("form.label.whatsapp")}
              </span>
              <span className="sr-only">{t("form.label.whatsapp")}</span>
            </Button>
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.PRIMARY} theme={ButtonTheme.WHITE} href="">
              <span className="flex items-center gap-xs ">
                <Icon name="phone" size="16" title="mail" />
                {t("form.label.call")}
              </span>
              <span className="sr-only">{t("form.label.call")}</span>
            </Button>
          </>
        )} */}
        <p className="mt-m text-s font-secondary">
          <TermsAndPrivacy translationKey="form.label.enquireterms" genericLeadForm={true} />
        </p>
      </div>
    </RecaptchaForm>
  );
}

export function isolatePrefix(phone: string | undefined) {
  if (!phone) {
    return null;
  }
  const prefixListSorted = prefixList.map((prefix) => prefix.dial_code).sort((a, b) => b.length - a.length);
  for (let prefix of prefixListSorted) {
    if (phone.startsWith(prefix)) {
      return prefix;
    }
  }
  return null;
}
